import Overlay from "ol/Overlay";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { fromLonLat, toLonLat } from "ol/proj";
import { Style, Stroke, Fill } from "ol/style";
import {
  fetchOverpassElements,
  buildShadowFeatures,
} from "./overpassShadows.esm.js";

const SunCalc = window.SunCalc;

/**
 * Initialisiert den Schattenwurf-Modus.
 * @param {Map} map - OpenLayers-Karte
 * @param {Object} options - z. B. { treeHeight: 10 }
 */
export function initShadow(map, options = {}) {
  const treeHeight = options.treeHeight || 10;
  let shadowCenter = null;
  let shadowActive = false;

  const shadowSource = new VectorSource();
  const shadowLayer = new VectorLayer({ source: shadowSource });
  shadowLayer.setVisible(false);
  shadowLayer.set("name", "shadowNormalLayer");

  map.addLayer(shadowLayer);

  // Persistenter Layer für die Overpass-Gebäude-/Baumschatten. Wird
  // einmal angelegt und nie wieder entfernt — nur seine Features werden
  // ersetzt. Vorher wurde bei jedem Update ein neuer Layer erzeugt und
  // per map.addLayer()/removeLayer() ausgetauscht, was (a) OL's
  // Attribution-Control mitten in einem Render-Frame einen gerade
  // hinzugefügten Layer suchen ließ, den es dort noch nicht kannte
  // (Absturz "Cannot read properties of undefined (reading 'visible')"),
  // und (b) bedeutete, dass jede Zeit-/Datumsänderung dieselben
  // Gebäudedaten erneut von Overpass angefragt hat.
  const shadowProSource = new VectorSource();
  const shadowProLayer = new VectorLayer({
    source: shadowProSource,
    style: new Style({
      fill: new Fill({ color: "rgba(0,0,0,0.33)" }),
      stroke: new Stroke({ color: "rgba(0,0,0,0.4)", width: 1 }),
    }),
    opacity: 0.4,
  });
  shadowProLayer.set("name", "shadowProLayer");
  shadowProLayer.setVisible(false);
  map.addLayer(shadowProLayer);

  /* ==============================
     Overpass-Geometrie-Cache
  ============================== */
  // Das Abfragen roher Gebäude-/Baumgeometrie von Overpass ist der
  // teure, ratenlimitierte Teil und hängt nur vom Ort ab, nicht von der
  // Uhrzeit. Wir merken uns die letzte erfolgreiche Abfrage und ihren
  // Mittelpunkt und fragen nur neu an, wenn sich der Schattenmittelpunkt
  // weit genug bewegt hat, um plausibel außerhalb der letzten Bounding
  // Box zu liegen. Eine reine Zeit-/Datumsänderung löst nie eine neue
  // Anfrage aus — sie berechnet die Schattenpolygone nur lokal aus der
  // bereits vorhandenen Geometrie neu.
  const GEOMETRY_BBOX_KM = 1.0;
  const REFETCH_DISTANCE_M = 150;
  let geometryCache = null; // { lon, lat, elements }
  let geometryFetchPromise = null; // laufende Anfrage, damit parallele Aufrufe sie teilen

  // Backoff nach einem fehlgeschlagenen Overpass-Fetch (429/504/…):
  // jeder aufeinanderfolgende Fehlschlag verdoppelt die Mindestwartezeit
  // bis zum nächsten Versuch (gedeckelt), statt sofort wieder in
  // dasselbe Rate-Limit zu laufen.
  const BASE_RETRY_MS = 20000;
  const MAX_RETRY_MS = 120000;
  let consecutiveFailures = 0;
  let nextFetchAllowedAt = 0;

  function distanceMeters([lon1, lat1], [lon2, lat2]) {
    // Grobe Näherung reicht für die Refetch-Entscheidung
    const dLat = (lat2 - lat1) * 111320;
    const dLon = (lon2 - lon1) * 111320 * Math.cos((lat1 * Math.PI) / 180);
    return Math.hypot(dLat, dLon);
  }

  function ensureGeometry(lon, lat) {
    const needsFetch =
      !geometryCache ||
      distanceMeters([geometryCache.lon, geometryCache.lat], [lon, lat]) >
        REFETCH_DISTANCE_M;

    if (!needsFetch) return Promise.resolve(geometryCache.elements);
    if (geometryFetchPromise) return geometryFetchPromise;

    if (Date.now() < nextFetchAllowedAt) {
      // Noch in der Backoff-Pause nach einem Fehler — lieber die alte
      // Geometrie (falls vorhanden) weiterverwenden als sofort erneut
      // anzufragen.
      return geometryCache
        ? Promise.resolve(geometryCache.elements)
        : Promise.reject(new Error("Overpass: Backoff aktiv"));
    }

    setLoading(true);
    geometryFetchPromise = fetchOverpassElements({
      lon,
      lat,
      bboxKm: GEOMETRY_BBOX_KM,
    })
      .then((elements) => {
        geometryCache = { lon, lat, elements };
        consecutiveFailures = 0;
        nextFetchAllowedAt = 0;
        return elements;
      })
      .catch((e) => {
        consecutiveFailures++;
        nextFetchAllowedAt =
          Date.now() +
          Math.min(
            BASE_RETRY_MS * 2 ** (consecutiveFailures - 1),
            MAX_RETRY_MS
          );
        console.error("Overpass/Schattenfehler:", e.message || e);
        // Alte Geometrie (falls vorhanden) weiterverwenden, damit der
        // zuletzt gezeigte Schatten stehen bleibt statt zu verschwinden.
        if (geometryCache) return geometryCache.elements;
        throw e;
      })
      .finally(() => {
        geometryFetchPromise = null;
        setLoading(false);
      });

    return geometryFetchPromise;
  }

  // --- HTML-Overlay vorbereiten ---
  const shadowDiv = document.createElement("div");
  shadowDiv.style.display = "none";

  // ☀️ Linie für Sonnenrichtung
  const sunLineDiv = document.createElement("div");
  sunLineDiv.className = "sunLine";
  sunLineDiv.style.display = "none";
  shadowDiv.appendChild(sunLineDiv);

  // 🌑 Linie für Schattenwurf
  const shadowLineDiv = document.createElement("div");
  shadowLineDiv.className = "shadowLine";
  shadowLineDiv.style.display = "none";
  shadowDiv.appendChild(shadowLineDiv);

  // 🔴 Punkt in der Mitte
  const shadowCenterDot = document.createElement("div");
  shadowCenterDot.className = "shadowCenterDot";
  shadowCenterDot.style.display = "none";
  shadowDiv.appendChild(shadowCenterDot);

  // Overlay für Karte
  const shadowOverlay = new Overlay({
    element: shadowDiv,
    positioning: "center-center",
    stopEvent: false,
  });
  map.addOverlay(shadowOverlay);

  // --- UI erzeugen ---
  const ui = document.createElement("div");
  const today = new Date();
  const currentHour = today.getHours();
  const currentMinute = today.getMinutes();

  ui.id = "schattenUI";
  ui.innerHTML = `
    <label>🕒 Stunde: <span id="shadowHourLabel">${currentHour}</span></label>
    <input type="range" id="shadowHour" min="0" max="23" value="${currentHour}" style="width:150px;">
    <br>
    <label>🕧 Minuten: <span id="shadowMinuteLabel">${currentMinute}</span></label>
    <input type="range" id="shadowMinute" min="0" max="59" value="${currentMinute}" style="width:150px;">
    <br>
    <label>📅 Datum:
      <input type="date" id="shadowDate" value="${
        today.toISOString().split("T")[0]
      }">
    </label>
    <div class="shadow-loading" id="shadowLoading">
      <span class="shadow-loading-spinner"></span>
      <span>Lädt Gebäude-/Baumdaten…</span>
    </div>
  `;
  ui.style.cssText = `
    position:absolute;top:10px;left:50%;transform:translateX(-50%);
    z-index:9999;background:rgba(255,255,255,0.95);
    padding:10px 20px;border-radius:10px;
    box-shadow:0 2px 6px rgba(0,0,0,0.2);
    display:none;
  `;
  document.body.appendChild(ui);

  const hourInput = ui.querySelector("#shadowHour");
  const hourLabel = ui.querySelector("#shadowHourLabel");
  const minuteInput = ui.querySelector("#shadowMinute");
  const minuteLabel = ui.querySelector("#shadowMinuteLabel");
  const dateInput = ui.querySelector("#shadowDate");
  const loadingIndicator = ui.querySelector("#shadowLoading");

  function setLoading(isLoading) {
    loadingIndicator.classList.toggle("visible", isLoading);
  }

  /** ======== Schattenberechnung ======== **/
  function updateShadow() {
    if (!shadowActive) return;

    removeLayerByName(map, "shadowNormalLayer");

    let lon, lat, coord3857;
    if (shadowCenter) {
      [lon, lat] = shadowCenter;
      coord3857 = fromLonLat(shadowCenter);
    } else {
      coord3857 = map.getView().getCenter();
      [lon, lat] = toLonLat(coord3857);
    }

    const date = new Date(dateInput.value);
    date.setHours(parseInt(hourInput.value, 10));
    date.setMinutes(parseInt(minuteInput.value, 10), 0, 0);

    const sun = SunCalc.getPosition(date, lat, lon);
    const altitude = sun.altitude;
    if (altitude <= 0) {
      shadowDiv.style.display = "none";
      shadowSource.clear();
      shadowProLayer.setVisible(false);
      return;
    }

    // Sichtbar schalten
    shadowDiv.style.display = "block";
    shadowCenterDot.style.display = "block";
    sunLineDiv.style.display = "block";
    shadowLineDiv.style.display = "block";

    // Richtung berechnen (0° = Norden, im Uhrzeigersinn)
    const shadowAngle = ((sun.azimuth * 180) / Math.PI + 270) % 360;
    const sunAngle = (shadowAngle + 180) % 360;

    // Layer aktualisieren
    shadowSource.clear();

    // Position Overlay
    shadowOverlay.setPosition(coord3857);

    // 🔄 Linienrotation – nur Richtung, Länge bleibt konstant
    sunLineDiv.style.transform = `rotate(${sunAngle}deg)`;
    shadowLineDiv.style.transform = `rotate(${shadowAngle}deg)`;

    shadowLayer.setZIndex(9998);
    shadowLayer.setVisible(true);

    /**** SHADOW PRO TEIL ******/
    shadowProLayer.setZIndex(9998);
    shadowProLayer.setVisible(true);

    ensureGeometry(lon, lat)
      .then((elements) => {
        if (!shadowActive) return; // in der Zwischenzeit deaktiviert
        const feats = buildShadowFeatures(elements, {
          lon,
          lat,
          datetime: date,
        });
        // Alte Features erst jetzt ersetzen, wenn die neuen bereit sind
        // — so bleibt der zuletzt berechnete Schatten sichtbar statt
        // beim Nachladen kurz zu verschwinden.
        shadowProSource.clear();
        shadowProSource.addFeatures(feats);
      })
      .catch(() => {
        // Fehler ist in ensureGeometry/fetchOverpassElements schon
        // geloggt; bisherige Features (falls vorhanden) bleiben
        // einfach stehen, damit die Anzeige nicht kaputt aussieht.
      });

    /**** SHADOE PRO ENDE ***** */
  }

  // Zeit-/Datumsschieber feuern beim Ziehen sehr viele "input"-Events —
  // ohne Debounce würde jedes davon updateShadow() (und damit ggf. eine
  // Overpass-Anfrage) auslösen. Das Label soll trotzdem sofort mitgehen.
  function debounce(fn, waitMs) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), waitMs);
    };
  }
  const debouncedUpdateShadow = debounce(updateShadow, 400);

  /** ======== Eventhandler ======== **/
  function onMoveEnd() {
    updateShadow();
  }
  function onMapClick(evt) {
    if (!shadowActive) return;
    shadowCenter = toLonLat(evt.coordinate);
    if (!evt.originalEvent.shiftKey) map.getView().setCenter(evt.coordinate);
    updateShadow();
  }

  /** ======== Button-Logik ======== **/
  const buttonMeasurementGui = document.getElementById("TestShadowButton");
  if (buttonMeasurementGui) {
    buttonMeasurementGui.addEventListener("click", () => {
      shadowActive = !shadowActive;
      if (shadowActive) {
        // Aktivieren
        buttonMeasurementGui.classList.remove("btn-success");
        buttonMeasurementGui.classList.add("btn-danger");
        buttonMeasurementGui.textContent = "Schattenwurf deaktivieren";
        ui.style.display = "block";
        map.on("moveend", onMoveEnd);
        map.on("click", onMapClick);
        shadowLayer.setVisible(true);
        updateShadow();
      } else {
        // Deaktivieren
        buttonMeasurementGui.classList.remove("btn-danger");
        buttonMeasurementGui.classList.add("btn-success");
        buttonMeasurementGui.textContent = "Schattenwurf aktivieren";
        ui.style.display = "none";
        shadowDiv.style.display = "none";
        shadowLineDiv.style.display = "none";
        sunLineDiv.style.display = "none";
        shadowCenterDot.style.display = "none";
        shadowLayer.setVisible(false);
        shadowSource.clear();
        shadowProLayer.setVisible(false);
        map.un("moveend", onMoveEnd);
        map.un("click", onMapClick);
        shadowCenter = null;
      }
    });
  }

  /** ======== UI-Events ======== **/
  hourInput.addEventListener("input", () => {
    hourLabel.textContent = hourInput.value;
    debouncedUpdateShadow();
  });
  minuteInput.addEventListener("input", () => {
    minuteLabel.textContent = minuteInput.value;
    debouncedUpdateShadow();
  });
  dateInput.addEventListener("change", debouncedUpdateShadow);
}

// Hilfsfunktionen, ggf in andereren SRC schon vorhanden

function getLayerByName(map, name) {
  return (
    map
      .getLayers()
      .getArray()
      .find((l) => l.get("name") === name) || null
  );
}

function removeLayerByName(map, name) {
  const lyr = getLayerByName(map, name);
  if (lyr) map.removeLayer(lyr);
  return !!lyr;
}
